import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import s from "./AppShell.module.css";
import { useSession } from "../app/SessionContext";
import { useNotifications } from "../api/queries";
import { Button, Select } from "../design/components";
import { ThemeProvider, useTheme } from "./theme";
import { DemoToolbar } from "../demo/DemoToolbar";
import { PlatformFeed } from "../demo/platform/PlatformFeed";
import { StoresFeed } from "../features/stores/StoresFeed";
import { FlightsFeed } from "../features/flights/FlightsFeed";
import { CertificatesFeed } from "../demo/platform/feeds/CertificatesFeed";
import { FeedNotice } from "../demo/platform/FeedNotice";
import { LegacyWrites } from "../demo/platform/LegacyWrites";
import { SafetyFeed } from "../demo/platform/feeds/SafetyFeed";
import { ClosuresFeed } from "../demo/platform/feeds/ClosuresFeed";
import { InspectionsFeed } from "../demo/platform/feeds/InspectionsFeed";
import { WriteNotice } from "../demo/platform/WriteNotice";
import { FrictionFeed } from "../demo/platform/feeds/FrictionFeed";
import { ApprovalsFeed } from "../demo/platform/feeds/ApprovalsFeed";
import { BoardsFeed } from "../demo/platform/feeds/BoardsFeed";
import { useCover } from "../demo/DemoGateway";
import { AssistantProvider } from "../assistant/AssistantContext";
import { canSee, useAccessLevel } from "../assistant/access";
import { principalKey } from "../assistant/preferences";
import { AccessGate } from "./AccessGate";
import { AssistantDock } from "../assistant/AssistantDock";
import { CallsProvider } from "../features/calls/CallsProvider";
import { attachNavigator } from "../demo/domain/domHost";
import { CallsMenuButton } from "../features/calls/CallsMenu";
import { Welcome } from "../assistant/Welcome";
import { CallView } from "../assistant/CallView";
import { Stage } from "../assistant/stage/Stage";
import { setRevealNav } from "../assistant/stage/anchors";
import { ScreenIntro } from "./ScreenIntro";
import { SUITES, suiteForPath } from "./suites";
import { groupOf, labelOf, SCREEN_BY_ROUTE, SCREENS } from "./navigation";
import { NavLayoutProvider, useNavLayout } from "./NavLayoutContext";
import { useRoomBar } from "./roomContext";
import { RouteBoundary } from "../app/crashGuard";
import { LiveRefreshProvider, useLiveRefresh } from "../app/LiveRefresh";
import { CustomizeMenuDialog } from "./CustomizeMenuDialog";
import { locale, t, tn, useI18n } from "../i18n";
import { DeskView } from "./DeskView";
import { watchDom } from "../i18n/dom";

/*
 * The menu is data (`navigation.ts`): every screen, the default suites, and the
 * person's own arrangement of them, kept on the platform. The dashboard's
 * boards are screens too (`dashboardScreens.ts`), each opening the dashboard on
 * that board, so SMS or the certification registers can be found from the menu.
 */

/*
 * A link is "current" for its own detail pages — /work-orders/WO-7 keeps Work
 * orders lit — but not for another screen that happens to sit beneath it:
 * /certification/assets is By asset, not Certification overview. So a screen
 * with other screens under it matches exactly.
 */
const PARENT_SCREENS: ReadonlySet<string> = new Set(
  SCREENS.filter((a) => SCREENS.some((b) => b.to !== a.to && b.to.startsWith(`${a.to}/`))).map((a) => a.to),
);

/*
 * Which suites are unfolded. Sixty screens in ten suites is taller than most
 * pages, so a suite starts folded to its heading and opens on a click; the
 * suite holding the screen on show opens by itself, so the current screen is
 * never hidden. Kept in this browser only — it is a habit of the hand, not a
 * setting worth carrying between devices.
 */
const EXPANDED_KEY = "nav.expanded";

function readExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function writeExpanded(ids: ReadonlySet<string>) {
  try {
    localStorage.setItem(EXPANDED_KEY, JSON.stringify([...ids]));
  } catch {
    /* private mode or storage off: the fold state lasts the session */
  }
}

function Icon({ d }: { d: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

export function AppShell() {
  return (
    <ThemeProvider>
      <NavLayoutProvider>
        <LiveRefreshProvider>
          <Shell />
        </LiveRefreshProvider>
      </NavLayoutProvider>
    </ThemeProvider>
  );
}

function Shell() {
  const session = useSession();
  const { theme, toggle, setTheme } = useTheme();
  const notifications = useNotifications();
  const unread = notifications.data?.filter((n) => !n.read).length ?? 0;
  const clock = useClock();
  const [aboutOpen, setAboutOpen] = useState<string | null>(null);
  const [customizing, setCustomizing] = useState(false);
  const { layout } = useNavLayout();
  const navigate = useNavigate();
  /* the ported boards' "open the buildings / airfield …" buttons open the app's screens */
  useEffect(() => {
    attachNavigator(navigate);
    return () => attachNavigator(null);
  }, [navigate]);
  const live = useLiveRefresh();
  const [expanded, setExpanded] = useState<Set<string>>(readExpanded);
  const currentSuite = suiteForPath(useLocation().pathname, layout)?.suite.id ?? null;
  useEffect(() => {
    if (!currentSuite || expanded.has(currentSuite)) return;
    setExpanded((prev) => {
      const next = new Set(prev).add(currentSuite);
      writeExpanded(next);
      return next;
    });
    // the suite of the screen on show opens; a later fold of it by hand stands until the next move
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSuite]);
  /* the access level in force: screens above it are not listed and not opened */
  const level = useAccessLevel(session.principal, principalKey(session.principal));
  /* a walkthrough that points at a menu link in a folded suite opens the suite first */
  useEffect(() => {
    setRevealNav((route) => {
      const g = groupOf(layout, route);
      if (!g) return;
      setExpanded((prev) => {
        if (prev.has(g.id)) return prev;
        const next = new Set(prev).add(g.id);
        writeExpanded(next);
        return next;
      });
    });
    return () => setRevealNav(null);
  }, [layout]);
  /* Arabic for the boards' generated HTML, while Arabic is chosen (i18n/dom.ts) */
  useEffect(() => watchDom(), []);
  const toggleSuite = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeExpanded(next);
      return next;
    });

  return (
    <AssistantProvider>
      {/* staff calls live above the screens, so a call survives moving between them;
          the window and the ring are drawn in a portal over everything */}
      <CallsProvider>
        <div className={s.shell}>
          <aside className={s.side} data-sky="sidebar">
            <div className={s.brand}>
              <div className={s.brandMark}>
                <svg viewBox="0 0 64 64" width="22" height="22" aria-hidden="true">
                  <path
                    d="M10 40h44M18 40l8-16h12l8 16"
                    stroke="#4C9BE8"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  />
                  <circle cx="32" cy="46" r="3" fill="#F2C230" />
                </svg>
              </div>
              <div>
                <div className={s.brandName}>Sky Logics</div>
                <div className={s.brandSub}>{t("Operations control")}</div>
              </div>
            </div>
            <nav aria-label="Primary" className={s.nav}>
              {layout.groups.map((group) => {
                const visible = group.screens.filter(
                  (to) => !layout.hidden.includes(to) && SCREEN_BY_ROUTE.has(to) && canSee(level, to),
                );
                if (visible.length === 0) return null;
                return (
                  <div key={group.id}>
                    <SuiteHead
                      id={group.id}
                      name={group.name}
                      expanded={expanded.has(group.id)}
                      onToggle={() => toggleSuite(group.id)}
                      aboutOpen={aboutOpen === group.id}
                      onToggleAbout={() => setAboutOpen((h) => (h === group.id ? null : group.id))}
                    />
                    {expanded.has(group.id) &&
                      visible.map((to) => (
                        <NavLink
                          key={to}
                          to={to}
                          end={to === "/" || PARENT_SCREENS.has(to)}
                          className={s.navLink}
                          data-sky-nav={to}
                        >
                          <Icon d={SCREEN_BY_ROUTE.get(to)!.icon} />
                          {t(labelOf(layout, to))}
                        </NavLink>
                      ))}
                  </div>
                );
              })}
              <button
                type="button"
                className={s.navCustomize}
                onClick={() => setCustomizing(true)}
                data-sky="customize"
              >
                <Icon d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0M14 4v4M8 10v4M16 16v4" />
                {t("Customize menu")}
              </button>
            </nav>
            {customizing && <CustomizeMenuDialog onClose={() => setCustomizing(false)} />}
            <div className={s.sideFoot}>
              <span>
                {t("Data:")} <b className="mono">{session.source.kind}</b>
                {session.source.kind === "platform" && (
                  <span
                    className={s.liveTag}
                    title={
                      live.live
                        ? t(
                            "Watching the platform's change feed every ten seconds; whatever is on screen refreshes when something changes",
                          )
                        : t("Not watching for changes: {reason}", { reason: live.reason ?? t("unknown") })
                    }
                  >
                    {live.live ? ` · ${t("live")}` : ` · ${t("not live")}`}
                  </span>
                )}
              </span>
              <span>Sky Logics Technologies 1.0</span>
            </div>
          </aside>

          <header className={s.head}>
            <div className={s.headSite} data-sky="site">
              <span className="eyebrow">{t("Site")}</span>
              {/* the ICAO code, which is how operators name a site; the full name is a hover away */}
              <Select
                value={session.site.id}
                onChange={(e) => session.selectSite(e.target.value)}
                aria-label={t("Site")}
                title={session.site.name}
              >
                {session.sites.map((site) => (
                  <option key={site.id} value={site.id} title={site.name}>
                    {site.code}
                  </option>
                ))}
              </Select>
            </div>
            <div className={s.headSpacer} />
            {/* search, the daylight view, and export/import of the work register —
            the demo carried these in its own header, and they apply everywhere */}
            <div data-sky="tools" style={{ display: "contents" }}>
              <DemoToolbar theme={theme} setTheme={setTheme} />
            </div>
            <span className={s.clock} title={t("Local time")}>
              {clock}
            </span>
            <span className={s.principal}>
              <b>{session.principal.name}</b>
              {session.tenant && <> · {session.tenant}</>}
              {session.developmentAuth && (
                <span className={s.devTag} title={t("The platform is accepting development authentication headers")}>
                  DEV AUTH
                </span>
              )}
            </span>
            <CallsButton />
            <Button
              variant="ghost"
              className={s.bell}
              aria-label={tn(unread, "{n} unread notification", "{n} unread notifications")}
              title={t("Notifications")}
            >
              <Icon d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 21h4" />
              {unread > 0 && <span className={s.bellDot} />}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/settings")}
              aria-label={t("Settings")}
              title={t("Settings")}
            >
              <Icon d="M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 01-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 01-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 01-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 010-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 012.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 014 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 012.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 010 4h-.1a1.7 1.7 0 00-1.5 1z" />
            </Button>
            <Button
              variant="ghost"
              onClick={toggle}
              aria-label={theme === "dark" ? t("Switch to light theme") : t("Switch to dark theme")}
              title={t("Theme")}
            >
              {theme === "dark" ? (
                <Icon d="M12 3a9 9 0 1 0 9 9 7 7 0 0 1-9-9z" />
              ) : (
                <Icon d="M12 4v2M12 18v2M4 12h2M18 12h2M6 6l1.5 1.5M16.5 16.5 18 18M6 18l1.5-1.5M16.5 7.5 18 6M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />
              )}
            </Button>
            <span data-sky="language" style={{ display: "contents" }}>
              <LanguageSwitch />
            </span>
            <SignOut />
          </header>

          <main className={s.main} data-sky="main">
            {session.fallbackReason && (
              <div className={s.banner} role="status">
                <span className={s.bannerTag}>{t("SAMPLE DATA")}</span>
                <span>
                  {t("The platform could not be reached, so this session is showing the built-in sample. Reason:")}{" "}
                  {session.fallbackReason}
                </span>
              </div>
            )}
            {!session.fallbackReason && session.source.kind === "mock" && (
              <div className={s.banner} role="status">
                <span className={s.bannerTag}>{t("SAMPLE DATA")}</span>
                <span>
                  {t("No platform configured — set VITE_SLT_API_URL to connect. Nothing shown here is real.")}
                </span>
              </div>
            )}
            <FeedNotice />
            <WriteNotice />
            <ScreenIntro />
            <AccessGate>
              <DeskView>
                <RouteBoundary>
                  <Outlet />
                </RouteBoundary>
              </DeskView>
            </AccessGate>
          </main>
          {/* pulls the platform's operational records into the boards' registers;
            renders nothing, and never causes the demo chunk to load */}
          {/* build 7: tells the boards they are on the live platform, first, and reads back their last registers */}
          <BoardsFeed />
          <PlatformFeed />
          {/* carries what the old boards record to the platform; renders nothing */}
          <LegacyWrites />
          <SafetyFeed />
          <ClosuresFeed />
          <CertificatesFeed />
          <InspectionsFeed />
          <FrictionFeed />
          <ApprovalsFeed />
          <StoresFeed />
          <FlightsFeed />
          {/* she sits above the shell so she survives navigation between screens */}
          <Welcome />
          <CallView />
          <AssistantDock />
          {/* her walkthroughs: the spotlight, the cursor and the caption */}
          <Stage />
        </div>
      </CallsProvider>
    </AssistantProvider>
  );
}

/**
 * Staff calls, from the header: counts the calls missed since the screen was
 * last opened, shows a moon while Do not disturb is on, and opens a small menu
 * — the screen, and Do not disturb. Absent for a sign-in not on staff calls.
 */
function CallsButton() {
  return (
    <CallsMenuButton
      buttonClass={s.bell}
      badgeClass={s.callBadge}
      icon={
        <Icon d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1 1 .4 1.9.7 2.8a2 2 0 01-.5 2.1L8 9.9a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.8.7a2 2 0 011.7 2z" />
      }
    />
  );
}

/**
 * Signing out.
 *
 * The cover has always carried "SIGN OUT INSTEAD", but nothing in the shell
 * ever opened the cover — `showCover` had no callers — so there was no way for
 * an operator to end a session short of closing the tab. On a platform whose
 * sign-in gate exists to attribute every action to a person, a session that
 * cannot be ended is the gap that matters.
 */
function SignOut() {
  const cover = useCover();
  if (!cover.user) return null;
  return (
    <Button variant="ghost" onClick={cover.signOut} aria-label={t("Sign out")} title={t("Sign out")}>
      <Icon d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l-5-5 5-5M5 12h12" />
    </Button>
  );
}

/**
 * English or Arabic. Shows the language it switches TO, in that language, so a
 * person who reads only one of them can always find their way back.
 */
function LanguageSwitch() {
  const { lang, setLang } = useI18n();
  const next = lang === "ar" ? "en" : "ar";
  return (
    <Button
      variant="ghost"
      className={s.langSwitch}
      onClick={() => setLang(next)}
      lang={next}
      aria-label={next === "ar" ? "العربية" : "English"}
      title={next === "ar" ? "العربية" : "English"}
    >
      {next === "ar" ? "عربي" : "EN"}
    </Button>
  );
}

/**
 * A sidebar suite heading. The name folds and unfolds the suite's screens; the
 * ⓘ beside it opens a few lines on what the suite is for, when it is one the
 * platform describes.
 */
function SuiteHead({
  id,
  name,
  expanded,
  onToggle,
  aboutOpen,
  onToggleAbout,
}: {
  id: string;
  name: string;
  expanded: boolean;
  onToggle: () => void;
  aboutOpen: boolean;
  onToggleAbout: () => void;
}) {
  const suite = SUITES.find((x) => x.id === id);
  return (
    <>
      <div className={s.navHeadRow}>
        <button
          type="button"
          className={`${s.navHead} ${s.navHeadButton}`}
          aria-expanded={expanded}
          data-sky-suite={id}
          title={expanded ? t("Fold this suite away") : t("Open this suite")}
          onClick={onToggle}
        >
          <span className={s.navChevron} aria-hidden="true">
            {expanded ? "▾" : "▸"}
          </span>
          {t(name)}
        </button>
        {suite && (
          <button
            type="button"
            className={s.navInfo}
            aria-label={t("About {name}", { name: t(name) })}
            aria-expanded={aboutOpen}
            title={aboutOpen ? t("Hide what this suite does") : t(suite.summary[0])}
            onClick={onToggleAbout}
          >
            {aboutOpen ? "−" : "ⓘ"}
          </button>
        )}
      </div>
      {aboutOpen && suite && <div className={s.navAbout}>{t(suite.tagline)}</div>}
    </>
  );
}

export function Page({
  eyebrow,
  title,
  actions,
  children,
}: {
  eyebrow?: string;
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const room = useRoomBar();
  if (room) {
    /* one tab of a room: the room's heading stands above, this screen's title is the tab's */
    return (
      <div className={s.page}>
        {room}
        <div className={s.pageHead} data-in-room>
          <div className={s.pageTitle}>
            <h2>{typeof title === "string" ? t(title) : title}</h2>
          </div>
          {actions && <div className={s.pageActions}>{actions}</div>}
        </div>
        {children}
      </div>
    );
  }
  return (
    <div className={s.page}>
      <div className={s.pageHead}>
        <div className={s.pageTitle}>
          {eyebrow && <div className="eyebrow">{t(eyebrow)}</div>}
          <h1>{typeof title === "string" ? t(title) : title}</h1>
        </div>
        {actions && <div className={s.pageActions}>{actions}</div>}
      </div>
      {children}
    </div>
  );
}

function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}
